import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlass as Search,
  ShoppingCart,
  X,
  Plus,
  Minus,
  Package,
  SmileyMeh,
  Storefront as StorefrontIcon,
  MapPin,
  InstagramLogo,
  FacebookLogo,
  TiktokLogo,
  WhatsappLogo,
  CheckCircle,
  ArrowLeft,
  Spinner,
  ClockCountdown,
  Clock,
  Truck,
  XCircle,
  GitBranch,
} from '@phosphor-icons/react';
import { storefrontAPI, getImageBaseUrl } from '../api';

// ── Font map ─────────────────────────────────────────────────────────────────
const FONT_MAP = {
  inter: '"Inter", sans-serif',
  poppins: '"Poppins", sans-serif',
  playfair: '"Playfair Display", serif',
  montserrat: '"Montserrat", sans-serif',
  raleway: '"Raleway", sans-serif',
  lato: '"Lato", sans-serif',
};

const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&family=Playfair+Display:wght@400;600;700&family=Montserrat:wght@400;500;600;700;800&family=Raleway:wght@400;500;600;700;800&family=Lato:wght@400;700&display=swap';

// ── Helpers ───────────────────────────────────────────────────────────────────
function getEffectivePrice(product) {
  if (!product.discountType || !product.discountValue) return product.sellingPrice;
  if (product.discountType === 'percentage')
    return product.sellingPrice * (1 - product.discountValue / 100);
  return Math.max(0, product.sellingPrice - product.discountValue);
}

function fmt(amount, symbol) {
  return `${symbol}${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── Skeleton ─────────────────────────────────────────────────────────────────
const Skeleton = () => (
  <div className="min-h-screen bg-white animate-pulse">
    <div className="h-14 bg-neutral-100" />
    <div className="h-64 bg-neutral-100" />
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
      <div className="flex gap-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-8 bg-neutral-100 rounded-full w-20" />
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="rounded-2xl overflow-hidden">
            <div className="aspect-square bg-neutral-100" />
            <div className="p-3 space-y-2">
              <div className="h-3 bg-neutral-100 rounded w-1/2" />
              <div className="h-4 bg-neutral-100 rounded w-3/4" />
              <div className="h-4 bg-neutral-100 rounded w-1/3" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Product Card ──────────────────────────────────────────────────────────────
const ProductCard = ({ product, store, onSelect, onAddToCart }) => {
  const imageBase = getImageBaseUrl();
  const effectivePrice = getEffectivePrice(product);
  const hasDiscount = effectivePrice < product.sellingPrice;
  const accent = store.accentColor || '#18181b';

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden border border-neutral-100 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer group"
      onClick={() => onSelect(product)}
    >
      <div className="aspect-square bg-neutral-50 overflow-hidden relative">
        {product.image ? (
          <img
            src={`${imageBase}${product.image}`}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-10 h-10 text-neutral-300" weight="duotone" />
          </div>
        )}
        {hasDiscount && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {product.discountType === 'percentage'
              ? `-${product.discountValue}%`
              : `-${store.currencySymbol}${product.discountValue}`}
          </span>
        )}
      </div>
      <div className="p-2.5 sm:p-3">
        {product.customCategory && (
          <p className="text-[10px] text-neutral-400 font-medium mb-0.5 truncate">
            {product.customCategory}
          </p>
        )}
        <p className="text-xs sm:text-sm font-semibold text-neutral-900 leading-tight line-clamp-2 mb-2">
          {product.name}
        </p>
        <div className="flex items-center justify-between gap-1">
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-bold text-neutral-900 truncate">
              {fmt(effectivePrice, store.currencySymbol)}
            </p>
            {hasDiscount && (
              <p className="text-[10px] text-neutral-400 line-through truncate">
                {fmt(product.sellingPrice, store.currencySymbol)}
              </p>
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onAddToCart(product, 1); }}
            className="w-7 h-7 rounded-full flex items-center justify-center hover:scale-110 active:scale-95 transition-transform flex-shrink-0 text-white"
            style={{ backgroundColor: accent }}
            aria-label={`Add ${product.name}`}
          >
            <Plus weight="bold" className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Product Modal ─────────────────────────────────────────────────────────────
const ProductModal = ({ product, store, onClose, onAddToCart }) => {
  const [qty, setQty] = useState(1);
  const imageBase = getImageBaseUrl();
  const effectivePrice = getEffectivePrice(product);
  const hasDiscount = effectivePrice < product.sellingPrice;
  const accent = store.accentColor || '#18181b';

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-black/10 flex items-center justify-center text-neutral-700 hover:bg-black/20 transition-colors"
        >
          <X weight="bold" className="w-4 h-4" />
        </button>

        <div className="aspect-video sm:aspect-square bg-neutral-50 flex-shrink-0">
          {product.image ? (
            <img src={`${imageBase}${product.image}`} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-16 h-16 text-neutral-300" weight="duotone" />
            </div>
          )}
        </div>

        <div className="p-5 flex flex-col gap-4 overflow-y-auto">
          {product.category && (
            <span className="inline-flex self-start text-xs font-semibold bg-neutral-100 text-neutral-500 px-2.5 py-1 rounded-full">
              {product.category.name}
            </span>
          )}
          <div>
            <h2 className="text-xl font-bold text-neutral-900 leading-snug mb-1">{product.name}</h2>
            {product.description && (
              <p className="text-sm text-neutral-500 leading-relaxed">{product.description}</p>
            )}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-neutral-900">
              {fmt(effectivePrice, store.currencySymbol)}
            </span>
            {hasDiscount && (
              <>
                <span className="text-base text-neutral-400 line-through">
                  {fmt(product.sellingPrice, store.currencySymbol)}
                </span>
                <span className="text-sm font-semibold text-red-500">
                  {product.discountType === 'percentage'
                    ? `${product.discountValue}% off`
                    : `${store.currencySymbol}${product.discountValue} off`}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 bg-neutral-100 rounded-full px-2 py-1">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-neutral-200 transition-colors disabled:opacity-30">
                <Minus weight="bold" className="w-4 h-4" />
              </button>
              <span className="w-6 text-center font-semibold text-sm">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-neutral-200 transition-colors">
                <Plus weight="bold" className="w-4 h-4" />
              </button>
            </div>
            <span className="text-sm text-neutral-400">
              {fmt(effectivePrice * qty, store.currencySymbol)} total
            </span>
          </div>
          <button
            onClick={() => { onAddToCart(product, qty); onClose(); }}
            className="w-full py-4 rounded-2xl font-bold text-sm text-white hover:opacity-90 active:scale-[0.98] transition-all"
            style={{ backgroundColor: accent }}
          >
            Add to Order – {fmt(effectivePrice * qty, store.currencySymbol)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Category Card ─────────────────────────────────────────────────────────────
const CategoryCard = ({ category, products, accent, selected, onViewAll }) => {
  const imageBase = getImageBaseUrl();
  const withImages = products.filter((p) => p.image);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    if (withImages.length < 2) return;
    const t = setInterval(() => setImgIdx((i) => (i + 1) % withImages.length), 2200);
    return () => clearInterval(t);
  }, [withImages.length]);

  const current = withImages[imgIdx] || products[0];

  return (
    <button
      onClick={onViewAll}
      className={`w-full rounded-2xl overflow-hidden border-2 text-left transition-all active:scale-[0.97] ${
        selected ? 'border-transparent shadow-lg' : 'border-neutral-100 shadow-sm hover:shadow-md'
      }`}
      style={selected ? { borderColor: accent } : {}}
    >
      <div className="aspect-square bg-neutral-50 overflow-hidden relative">
        {current?.image ? (
          <img
            key={imgIdx}
            src={`${imageBase}${current.image}`}
            alt={category.name}
            className="w-full h-full object-cover animate-fadeIn"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-8 h-8 text-neutral-200" weight="duotone" />
          </div>
        )}
        {selected && (
          <div className="absolute inset-0 opacity-10" style={{ backgroundColor: accent }} />
        )}
      </div>
      <div className="p-2.5 bg-white">
        <p className="text-xs font-bold text-neutral-900 truncate">{category.name}</p>
        <p className="text-[10px] text-neutral-400 mt-0.5">{products.length} item{products.length !== 1 ? 's' : ''}</p>
        <p className="text-[10px] font-semibold mt-1.5" style={{ color: accent }}>See All →</p>
      </div>
    </button>
  );
};

// ── Phone validator ───────────────────────────────────────────────────────────
function isValidPhone(phone) {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 9 && digits.length <= 15;
}

// ── Branch Picker (SPLIT mode landing page) ───────────────────────────────────
const BranchPicker = ({ store, branches, onSelect }) => {
  const accent = store.accentColor || '#18181b';
  return (
    <div className="min-h-screen bg-neutral-50" style={{ fontFamily: FONT_MAP[store.fontFamily] || FONT_MAP.inter }}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-neutral-100">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-2.5">
          {store.businessLogo ? (
            <img src={`${getImageBaseUrl()}${store.businessLogo}`} alt={store.businessName}
              className="w-8 h-8 rounded-xl object-cover flex-shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: accent }}>
              <StorefrontIcon className="w-4 h-4 text-white" weight="fill" />
            </div>
          )}
          <span className="font-bold text-neutral-900 truncate text-sm sm:text-base">{store.businessName}</span>
        </div>
      </header>

      {/* Hero */}
      {store.heroImage && (
        <div className="h-40 sm:h-64 overflow-hidden relative">
          <img src={`${getImageBaseUrl()}${store.heroImage}`} alt="Store banner" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/60" />
          <div className="absolute bottom-0 left-0 right-0 px-4 py-5 max-w-5xl mx-auto">
            <h1 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight drop-shadow">{store.businessName}</h1>
            {store.tagline && <p className="text-white/80 text-sm mt-1 drop-shadow">{store.tagline}</p>}
          </div>
        </div>
      )}

      <main className="max-w-2xl mx-auto px-4 py-8">
        {!store.heroImage && (
          <div className="mb-6">
            <h1 className="text-2xl font-extrabold text-neutral-900">{store.businessName}</h1>
            {store.tagline && <p className="text-neutral-500 text-sm mt-1">{store.tagline}</p>}
          </div>
        )}
        <h2 className="text-base font-bold text-neutral-900 mb-4">Select a branch to order from</h2>
        <div className="space-y-3">
          {branches.map((b) => (
            <button
              key={b.id}
              onClick={() => b.isEnabled && onSelect(b.slug)}
              disabled={!b.isEnabled}
              className={`w-full flex items-center gap-4 p-4 bg-white rounded-2xl border-2 text-left transition-all active:scale-[0.98] ${
                b.isEnabled
                  ? 'border-neutral-100 hover:border-neutral-300 cursor-pointer'
                  : 'border-neutral-100 opacity-50 cursor-not-allowed'
              }`}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: b.accentColor || accent + '20' }}>
                <GitBranch className="w-5 h-5" style={{ color: b.accentColor || accent }} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-neutral-900 text-sm">{b.name}</p>
                {b.address && <p className="text-xs text-neutral-500 mt-0.5 truncate">📍 {b.address}</p>}
              </div>
              {b.isEnabled ? (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex-shrink-0">Open</span>
              ) : (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-400 flex-shrink-0">Closed</span>
              )}
            </button>
          ))}
        </div>
      </main>

      <footer className="mt-8 pb-12 text-center">
        <a href="https://smartposafrica.com" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-600 transition-colors">
          <span>Powered by</span>
          <span className="font-bold text-neutral-600">SmartPOS Africa</span>
        </a>
      </footer>
    </div>
  );
};

// ── Also Available At Strip ───────────────────────────────────────────────────
const AlsoAvailableAt = ({ branches, currentBranchSlug, slug, onSwitchBranch, accent }) => {
  const others = branches.filter((b) => b.slug !== currentBranchSlug && b.isEnabled);
  if (others.length === 0) return null;
  return (
    <section className="max-w-5xl mx-auto px-4 py-6 border-t border-neutral-100">
      <h3 className="text-sm font-bold text-neutral-700 mb-3">Also available at</h3>
      <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
        {others.map((b) => (
          <button
            key={b.id}
            onClick={() => onSwitchBranch(b.slug)}
            className="flex-shrink-0 flex items-center gap-2.5 px-4 py-3 bg-white rounded-2xl border border-neutral-100 hover:border-neutral-300 active:scale-[0.97] transition-all text-left"
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent + '15' }}>
              <GitBranch className="w-4 h-4" style={{ color: accent }} weight="bold" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-neutral-900 truncate">{b.name}</p>
              {b.address && <p className="text-xs text-neutral-500 truncate max-w-[140px]">{b.address}</p>}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

// ── Order Form ────────────────────────────────────────────────────────────────
const OrderForm = ({ cart, store, branchSlug, onClose, onOrderPlaced }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const accent = store.accentColor || '#18181b';
  const total = cart.reduce((s, i) => s + getEffectivePrice(i) * i.quantity, 0);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Please enter your name.'); return; }
    if (!form.phone.trim()) { setError('Please enter your phone number.'); return; }
    if (!isValidPhone(form.phone)) { setError('Please enter a valid phone number (e.g. 0551234567).'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await storefrontAPI.placeOrder(store.slug, {
        customerName: form.name,
        customerPhone: form.phone,
        customerEmail: form.email || undefined,
        deliveryAddress: form.address || undefined,
        notes: form.notes || undefined,
        branchSlug: branchSlug || undefined,
        items: cart.map((i) => ({ productId: i.id, quantity: i.quantity })),
      });
      onOrderPlaced(res.data.order);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:border-transparent transition';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
          <h2 className="text-lg font-bold text-neutral-900">Complete Your Order</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
            <X weight="bold" className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        {/* Order summary */}
        <div className="px-5 py-3 bg-neutral-50 border-b border-neutral-100">
          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
            {cart.reduce((s, i) => s + i.quantity, 0)} items · {fmt(total, store.currencySymbol)}
          </p>
          <div className="space-y-1 max-h-20 overflow-y-auto">
            {cart.map((item) => (
              <p key={item.id} className="text-xs text-neutral-600">
                {item.quantity}× {item.name} – {fmt(getEffectivePrice(item) * item.quantity, store.currencySymbol)}
              </p>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3 p-5 overflow-y-auto">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="text-xs font-semibold text-neutral-600 mb-1 block">Name *</label>
              <input className={inputCls} placeholder="Your name" value={form.name} onChange={set('name')} style={{ '--tw-ring-color': accent + '30' }} />
            </div>
            <div className="flex-1">
              <label className="text-xs font-semibold text-neutral-600 mb-1 block">Phone *</label>
              <input className={inputCls} placeholder="0XX XXX XXXX" value={form.phone} onChange={set('phone')} type="tel" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Email <span className="text-neutral-400 font-normal">(optional)</span></label>
            <input className={inputCls} placeholder="you@example.com" value={form.email} onChange={set('email')} type="email" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Delivery Address <span className="text-neutral-400 font-normal">(optional)</span></label>
            <input className={inputCls} placeholder="Where should we deliver?" value={form.address} onChange={set('address')} />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Notes <span className="text-neutral-400 font-normal">(optional)</span></label>
            <textarea className={`${inputCls} resize-none`} rows={2} placeholder="Special instructions..." value={form.notes} onChange={set('notes')} />
          </div>

          {error && <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-xl">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 rounded-2xl font-bold text-sm text-white flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60"
            style={{ backgroundColor: accent }}
          >
            {submitting ? (
              <><Spinner className="w-4 h-4 animate-spin" /> Placing Order...</>
            ) : (
              <>Place Order · {fmt(total, store.currencySymbol)}</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Order Confirmation ────────────────────────────────────────────────────────
const OrderConfirmation = ({ order, store, onClose, onTrack }) => {
  const accent = store.accentColor || '#18181b';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white w-full max-w-sm rounded-3xl p-8 text-center shadow-2xl">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: accent + '15' }}>
          <CheckCircle className="w-8 h-8" style={{ color: accent }} weight="fill" />
        </div>
        <h2 className="text-xl font-extrabold text-neutral-900 mb-1">Order Placed!</h2>
        <p className="text-sm text-neutral-500 mb-4">
          We've received your order and will confirm it shortly.
        </p>
        <div className="bg-neutral-50 rounded-2xl px-4 py-3 mb-4">
          <p className="text-xs text-neutral-400 mb-0.5">Order Reference</p>
          <p className="text-2xl font-extrabold tracking-wider" style={{ color: accent }}>
            {order.orderNumber}
          </p>
        </div>
        <button
          onClick={onTrack}
          className="w-full py-3 rounded-2xl font-bold text-sm mb-3 border-2 transition-colors"
          style={{ borderColor: accent, color: accent }}
        >
          Track My Order
        </button>
        <button
          onClick={onClose}
          className="w-full py-3 rounded-2xl font-bold text-sm text-white"
          style={{ backgroundColor: accent }}
        >
          Continue Browsing
        </button>
      </div>
    </div>
  );
};

// ── Track Order Modal ─────────────────────────────────────────────────────────
const STATUS_STEPS = ['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'DELIVERED'];
const STATUS_LABELS = {
  PENDING:   { label: 'Order Placed',  icon: Clock },
  CONFIRMED: { label: 'Confirmed',     icon: CheckCircle },
  PREPARING: { label: 'Preparing',     icon: Package },
  READY:     { label: 'Ready',         icon: CheckCircle },
  DELIVERED: { label: 'Delivered',     icon: Truck },
  CANCELLED: { label: 'Cancelled',     icon: XCircle },
};

// ── Single Order Status View ──────────────────────────────────────────────────
const OrderStatusView = ({ order, store, onBack = null }) => {
  const accent = store.accentColor || '#18181b';
  const isCancelled = order.status === 'CANCELLED';
  const stepIdx = isCancelled ? -1 : STATUS_STEPS.indexOf(order.status);

  return (
    <div className="space-y-4">
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-neutral-500 hover:text-neutral-800 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" weight="bold" /> All results
        </button>
      )}

      <div className="bg-neutral-50 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-neutral-400">Order Reference</p>
          <p className="text-sm font-bold tracking-wide" style={{ color: accent }}>{order.orderNumber}</p>
        </div>
        <p className="text-sm font-semibold text-neutral-900">{order.customerName}</p>
        <p className="text-xs text-neutral-500 mt-0.5">{order.customerPhone}</p>
        {order.deliveryAddress && (
          <p className="text-xs text-neutral-500 mt-0.5">📍 {order.deliveryAddress}</p>
        )}
      </div>

      {isCancelled ? (
        <div className="flex items-center gap-3 p-4 bg-red-50 rounded-2xl">
          <XCircle className="w-8 h-8 text-red-400 flex-shrink-0" weight="fill" />
          <div>
            <p className="font-bold text-red-600">Order Cancelled</p>
            <p className="text-xs text-red-400 mt-0.5">This order has been cancelled.</p>
          </div>
        </div>
      ) : (
        <div className="bg-neutral-50 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Order Status</p>
            {!TERMINAL_STATUSES.includes(order.status) && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
            )}
          </div>
          <div className="space-y-0">
            {STATUS_STEPS.map((s, i) => {
              const cfg = STATUS_LABELS[s];
              const Icon = cfg.icon;
              const done = i <= stepIdx;
              const active = i === stepIdx;
              return (
                <div key={s} className="flex items-start gap-3">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
                      style={done ? { backgroundColor: accent, color: '#fff' } : { backgroundColor: '#f3f4f6', color: '#9ca3af' }}>
                      <Icon className="w-4 h-4" weight={done ? 'fill' : 'regular'} />
                    </div>
                    {i < STATUS_STEPS.length - 1 && (
                      <div className="w-0.5 h-6" style={{ backgroundColor: i < stepIdx ? accent : '#e5e7eb' }} />
                    )}
                  </div>
                  <div className="pt-1 pb-5">
                    <p className={`text-sm leading-none ${active ? 'font-bold text-neutral-900' : done ? 'font-semibold text-neutral-600' : 'text-neutral-400'}`}>
                      {cfg.label}
                      {active && <span className="ml-2 text-[10px] font-semibold rounded-full px-1.5 py-0.5 text-white" style={{ backgroundColor: accent }}>Now</span>}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-neutral-50 rounded-2xl p-4">
        <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-3">Items</p>
        <div className="space-y-2">
          {order.items?.map((item) => (
            <div key={item.id} className="flex justify-between items-center text-sm gap-2">
              <span className="text-neutral-700 truncate flex-1">{item.quantity}× {item.productName}</span>
              <span className="font-semibold text-neutral-900 flex-shrink-0">{fmt(item.total, store.currencySymbol)}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm font-bold pt-2 border-t border-neutral-100 mt-1">
            <span>Total</span>
            <span>{fmt(order.total, store.currencySymbol)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const TERMINAL_STATUSES = ['DELIVERED', 'CANCELLED'];

const TrackOrderModal = ({ store, onClose, prefillOrderNumber, prefillPhone }) => {
  const accent = store.accentColor || '#18181b';
  const [orderNumber, setOrderNumber] = useState(prefillOrderNumber || '');
  const [phone, setPhone] = useState(prefillPhone || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);       // array of orders
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Auto-search when prefilled
  useEffect(() => {
    if (prefillPhone && prefillOrderNumber) {
      storefrontAPI.trackOrder(store.slug, prefillPhone, prefillOrderNumber)
        .then((res) => {
          const orders = res.data.orders || [];
          setResults(orders);
          if (orders.length === 1) setSelectedOrder(orders[0]);
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  // Real-time polling – refresh while order is in a non-terminal state
  useEffect(() => {
    if (!selectedOrder || TERMINAL_STATUSES.includes(selectedOrder.status)) return;

    const { id, customerPhone, orderNumber: ordNum } = selectedOrder;
    const poll = async () => {
      try {
        const res = await storefrontAPI.trackOrder(store.slug, customerPhone, ordNum);
        const updated = (res.data.orders || []).find((o) => o.id === id);
        if (updated) {
          setSelectedOrder(updated);
          setResults((prev) => prev ? prev.map((o) => o.id === id ? updated : o) : prev);
        }
      } catch { /* silent */ }
    };

    const intervalId = setInterval(poll, 8000);
    return () => clearInterval(intervalId);
  }, [selectedOrder?.id, selectedOrder?.status, store.slug]); // eslint-disable-line

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { setError('Phone number is required.'); return; }
    setLoading(true);
    setError('');
    setResults(null);
    setSelectedOrder(null);
    try {
      const res = await storefrontAPI.trackOrder(store.slug, phone.trim(), orderNumber.trim() || undefined);
      const orders = res.data.orders || [];
      setResults(orders);
      if (orders.length === 1) setSelectedOrder(orders[0]);
    } catch (err) {
      setError(err.response?.data?.error || 'No orders found. Check your phone number and try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="sm:hidden w-10 h-1 bg-neutral-200 rounded-full mx-auto mt-3 mb-1 flex-shrink-0" />
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 flex-shrink-0">
          <h2 className="text-base font-bold text-neutral-900">Track Your Order</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
            <X weight="bold" className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">

          {/* Search form */}
          <form onSubmit={handleTrack} className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-neutral-600 mb-1.5 block">Phone Number *</label>
              <input className={inputCls} style={{ '--tw-ring-color': accent + '30' }}
                placeholder="0551234567" value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600 mb-1.5 block">
                Order Number <span className="text-neutral-400 font-normal">(optional – narrows results)</span>
              </label>
              <input className={`${inputCls} uppercase tracking-wider`} style={{ '--tw-ring-color': accent + '30' }}
                placeholder="ORD-00001" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value.toUpperCase())} />
            </div>
            {error && <p className="text-xs text-red-500 font-medium">{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-2xl font-bold text-sm text-white flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60"
              style={{ backgroundColor: accent }}>
              {loading ? <Spinner className="w-4 h-4 animate-spin" /> : 'Find My Order'}
            </button>
          </form>

          {/* Multiple results list */}
          {results && !selectedOrder && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                {results.length} order{results.length !== 1 ? 's' : ''} found
              </p>
              {results.map((order) => {
                const cfg = STATUS_LABELS[order.status] || STATUS_LABELS.PENDING;
                const Icon = cfg?.icon || Clock;
                const statusColor = {
                  PENDING:   'bg-yellow-100 text-yellow-700',
                  CONFIRMED: 'bg-blue-100 text-blue-700',
                  PREPARING: 'bg-purple-100 text-purple-700',
                  READY:     'bg-green-100 text-green-700',
                  DELIVERED: 'bg-neutral-100 text-neutral-600',
                  CANCELLED: 'bg-red-100 text-red-500',
                }[order.status] || 'bg-neutral-100 text-neutral-500';

                return (
                  <button key={order.id} onClick={() => setSelectedOrder(order)}
                    className="w-full flex items-center gap-3 p-4 bg-neutral-50 rounded-2xl hover:bg-neutral-100 active:scale-[0.99] transition-all text-left">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-neutral-900">{order.orderNumber}</p>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        {order.items?.length} item{order.items?.length !== 1 ? 's' : ''} · {fmt(order.total, store.currencySymbol)}
                      </p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1 flex-shrink-0 ${statusColor}`}>
                      <Icon className="w-3 h-3" weight="bold" />
                      {cfg.label}
                    </span>
                    <ArrowLeft className="w-4 h-4 text-neutral-400 rotate-180 flex-shrink-0" weight="bold" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Single order detail */}
          {selectedOrder && (
            <OrderStatusView
              order={selectedOrder} store={store}
              onBack={results?.length > 1 ? () => setSelectedOrder(null) : undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ── Cart Drawer ───────────────────────────────────────────────────────────────
const CartDrawer = ({ cart, store, onClose, onUpdateQty, onRemove, onCheckout }) => {
  const total = cart.reduce((s, i) => s + getEffectivePrice(i) * i.quantity, 0);
  const accent = store.accentColor || '#18181b';

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end sm:flex-row sm:justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-none max-h-[85vh] sm:max-h-none sm:h-full flex flex-col shadow-2xl">
        <div className="sm:hidden w-10 h-1 bg-neutral-200 rounded-full mx-auto mt-3 mb-1 flex-shrink-0" />
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-neutral-900">Your Order</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
            <X weight="bold" className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
              <ShoppingCart className="w-14 h-14 text-neutral-200" weight="duotone" />
              <p className="text-neutral-400 text-sm">Your cart is empty.</p>
            </div>
          ) : (
            cart.map((item) => {
              const price = getEffectivePrice(item);
              return (
                <div key={item.id} className="flex items-start gap-3">
                  <div className="flex items-center gap-2 bg-neutral-100 rounded-full px-2 py-1 flex-shrink-0">
                    <button onClick={() => onUpdateQty(item.id, item.quantity - 1)}
                      className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-neutral-200 transition-colors">
                      <Minus weight="bold" className="w-3 h-3" />
                    </button>
                    <span className="w-4 text-center text-sm font-semibold">{item.quantity}</span>
                    <button onClick={() => onUpdateQty(item.id, item.quantity + 1)}
                      className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-neutral-200 transition-colors">
                      <Plus weight="bold" className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-neutral-900 line-clamp-2 leading-tight">{item.name}</p>
                    <p className="text-xs text-neutral-400 mt-0.5">{fmt(price, store.currencySymbol)} each</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-sm font-bold text-neutral-900">
                      {fmt(price * item.quantity, store.currencySymbol)}
                    </span>
                    <button onClick={() => onRemove(item.id)} className="text-xs text-neutral-400 hover:text-red-500 transition-colors">
                      Remove
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-neutral-100 px-5 py-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-500">Total</span>
              <span className="text-xl font-extrabold text-neutral-900">{fmt(total, store.currencySymbol)}</span>
            </div>
            <button
              onClick={onCheckout}
              className="w-full py-4 rounded-2xl font-bold text-sm text-white hover:opacity-90 active:scale-[0.98] transition-all"
              style={{ backgroundColor: accent }}
            >
              Place Order
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Preview Banner ────────────────────────────────────────────────────────────
const PreviewBanner = ({ accent }) => (
  <div className="sticky top-14 z-50 flex items-center justify-between gap-3 px-4 py-2.5 text-white text-xs font-semibold" style={{ backgroundColor: accent }}>
    <span>👁 You're previewing your store</span>
    <button
      onClick={() => window.close()}
      className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/20 hover:bg-white/30 transition-colors active:scale-95 whitespace-nowrap"
      style={{ touchAction: 'manipulation' }}
    >
      <ArrowLeft className="w-3 h-3" weight="bold" />
      Exit Preview
    </button>
  </div>
);

// ── Main Storefront ───────────────────────────────────────────────────────────
const Storefront = () => {
  const { slug, branchSlug } = useParams();
  const navigate = useNavigate();
  const imageBase = getImageBaseUrl();
  const isPreview = new URLSearchParams(window.location.search).get('preview') === '1';

  const [store, setStore] = useState(null);
  const [storefrontMode, setStorefrontMode] = useState('UNIFIED');
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredIds, setFeaturedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState(null);
  const [switchWarning, setSwitchWarning] = useState(null); // branch to switch to

  const [view, setView] = useState('home'); // 'home' | 'browse'
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orderFormOpen, setOrderFormOpen] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [trackOpen, setTrackOpen] = useState(false);
  const [trackPrefill, setTrackPrefill] = useState({ orderNumber: '', phone: '' });

  // Load store info – handles both unified and branch-specific
  useEffect(() => {
    setLoading(true);
    const loadStore = branchSlug
      ? storefrontAPI.getBranchStore(slug, branchSlug)
      : storefrontAPI.getStore(slug);

    loadStore
      .then((res) => {
        setStorefrontMode(res.data.storefrontMode || 'UNIFIED');
        setStore(res.data.store);
        if (res.data.branches) setBranches(res.data.branches);
        // If SPLIT mode with no branchSlug → show branch picker (store is still set for the picker)
      })
      .catch(() => setError('This store could not be found or is currently unavailable.'))
      .finally(() => setLoading(false));
  }, [slug, branchSlug]);

  // Inject Google Fonts
  useEffect(() => {
    if (!document.querySelector('#storefront-fonts')) {
      const link = document.createElement('link');
      link.id = 'storefront-fonts';
      link.rel = 'stylesheet';
      link.href = GOOGLE_FONTS_URL;
      document.head.appendChild(link);
    }
  }, []);

  // Load all products once – filtering is client-side
  const fetchProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const params = branchSlug ? { branchSlug } : {};
      const res = await storefrontAPI.getProducts(slug, params);
      setProducts(res.data.products || []);
      if (res.data.categories?.length) setCategories(res.data.categories);
      if (res.data.featuredProductIds?.length) setFeaturedIds(res.data.featuredProductIds);
    } catch {
      // silent fail
    } finally {
      setLoadingProducts(false);
    }
  }, [slug, branchSlug]);

  useEffect(() => {
    // Only fetch products if we have a store AND (unified OR we have a branchSlug)
    if (store && (storefrontMode === 'UNIFIED' || branchSlug)) fetchProducts();
  }, [store, storefrontMode, branchSlug, fetchProducts]);

  // Branch switch – clear cart and navigate to new branch
  const handleBranchSwitch = useCallback((targetBranchSlug) => {
    if (cart.length > 0) {
      setSwitchWarning(targetBranchSlug);
    } else {
      navigate(`/store/${slug}/${targetBranchSlug}`);
    }
  }, [cart, slug, navigate]);

  const confirmBranchSwitch = useCallback((targetBranchSlug) => {
    setCart([]);
    setSwitchWarning(null);
    navigate(`/store/${slug}/${targetBranchSlug}`);
  }, [slug, navigate]);

  // Cart helpers
  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const ex = prev.find((i) => i.id === product.id);
      if (ex) return prev.map((i) => i.id === product.id ? { ...i, quantity: i.quantity + qty } : i);
      return [...prev, { ...product, quantity: qty }];
    });
  }, []);

  const updateQty = useCallback((id, qty) => {
    if (qty <= 0) setCart((prev) => prev.filter((i) => i.id !== id));
    else setCart((prev) => prev.map((i) => i.id === id ? { ...i, quantity: qty } : i));
  }, []);

  const removeItem = useCallback((id) => setCart((prev) => prev.filter((i) => i.id !== id)), []);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  const toggleCategory = useCallback((catId) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const goHome = useCallback(() => {
    setView('home');
    setSelectedCategories(new Set());
    setSearch('');
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const browseTo = useCallback((catId) => {
    if (catId) {
      setSelectedCategories(new Set([catId]));
    } else {
      setSelectedCategories(new Set());
    }
    setView('browse');
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  if (loading) return <Skeleton />;
  if (error) return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-6 text-center">
      <SmileyMeh className="w-16 h-16 text-neutral-300" weight="duotone" />
      <h1 className="text-xl font-bold text-neutral-900">Store not found</h1>
      <p className="text-neutral-500 text-sm max-w-xs">{error}</p>
    </div>
  );

  // SPLIT mode with no branchSlug → show branch picker
  if (storefrontMode === 'SPLIT' && !branchSlug && store) {
    return <BranchPicker store={store} branches={branches} onSelect={(bSlug) => navigate(`/store/${slug}/${bSlug}`)} />;
  }

  const font = FONT_MAP[store.fontFamily] || FONT_MAP.inter;
  const accent = store.accentColor || '#18181b';

  // Featured products
  const featuredProducts = featuredIds.map((id) => products.find((p) => p.id === id)).filter(Boolean);

  // Products grouped by customCategory string
  const productsByCategory = {};
  products.forEach((p) => {
    if (p.customCategory) {
      if (!productsByCategory[p.customCategory]) productsByCategory[p.customCategory] = [];
      productsByCategory[p.customCategory].push(p);
    }
  });

  // Client-side filtered product list
  const displayProducts = (() => {
    let list = products;
    if (selectedCategories.size > 0) {
      list = list.filter((p) => selectedCategories.has(p.customCategory));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }
    return list;
  })();

  return (
    <div style={{ fontFamily: font }} className="min-h-screen bg-neutral-50">

      {/* ── Sticky Header ─────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-neutral-100">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 min-w-0">
            {store.businessLogo ? (
              <img src={`${imageBase}${store.businessLogo}`} alt={store.businessName}
                className="w-8 h-8 rounded-xl object-cover flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: accent }}>
                <StorefrontIcon className="w-4 h-4 text-white" weight="fill" />
              </div>
            )}
            <div className="min-w-0">
              <span className="font-bold text-neutral-900 truncate text-sm sm:text-base block">
                {store.branchName || store.businessName}
              </span>
              {store.branchName && (
                <span className="text-xs text-neutral-400 truncate block leading-tight">{store.businessName}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => { setTrackPrefill({ orderNumber: '', phone: '' }); setTrackOpen(true); }}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border-2 hover:opacity-80 transition-all"
              style={{ borderColor: accent, color: accent }}
            >
              <ClockCountdown weight="bold" className="w-4 h-4" />
              Track Order
            </button>
            <button
              onClick={() => setCartOpen(true)}
              className="relative flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold text-white hover:opacity-90 active:scale-95 transition-all"
              style={{ backgroundColor: accent }}
            >
              <ShoppingCart weight="bold" className="w-4 h-4" />
              {cartCount > 0
                ? <span>{cartCount} item{cartCount !== 1 ? 's' : ''}</span>
                : <span className="hidden sm:inline">Cart</span>
              }
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ── Preview Banner ────────────────────────────────────────────── */}
      {isPreview && <PreviewBanner accent={accent} />}

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      {view === 'home' && <div className="relative">
        {store.heroImage ? (
          <div className="h-56 sm:h-80 overflow-hidden">
            <img src={`${imageBase}${store.heroImage}`} alt="Store banner"
              className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />
          </div>
        ) : (
          <div className="h-32 sm:h-48" style={{ backgroundColor: accent + '18' }} />
        )}

        <div className={`${store.heroImage ? 'absolute bottom-0 left-0 right-0' : ''} px-4 py-5`}>
          <div className="max-w-5xl mx-auto">
            {store.heroImage ? (
              <>
                <h1 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight drop-shadow">
                  {store.branchName || store.businessName}
                </h1>
                {store.branchName && (
                  <p className="text-white/70 text-xs sm:text-sm mt-0.5 drop-shadow font-medium">{store.businessName}</p>
                )}
                {store.tagline && (
                  <p className="text-white/80 text-sm sm:text-base mt-1 drop-shadow">{store.tagline}</p>
                )}
              </>
            ) : (
              <>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-neutral-900 tracking-tight">
                  {store.branchName || store.businessName}
                </h1>
                {store.branchName && (
                  <p className="text-neutral-400 text-xs sm:text-sm mt-0.5 font-medium">{store.businessName}</p>
                )}
                {store.tagline && (
                  <p className="text-neutral-500 text-sm sm:text-base mt-1">{store.tagline}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>}

      {/* ── About strip ───────────────────────────────────────────────── */}
      {view === 'home' && (store.description || store.instagramUrl || store.facebookUrl || store.tiktokUrl || store.whatsappNumber) && (
        <div className="bg-white border-b border-neutral-100">
          <div className="max-w-5xl mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            {store.description && (
              <p className="text-sm text-neutral-500 max-w-lg">{store.description}</p>
            )}
            <div className="flex items-center gap-3 flex-shrink-0">
              {store.instagramUrl && (
                <a href={store.instagramUrl} target="_blank" rel="noopener noreferrer"
                  className="text-neutral-400 hover:text-pink-500 transition-colors">
                  <InstagramLogo className="w-5 h-5" weight="fill" />
                </a>
              )}
              {store.facebookUrl && (
                <a href={store.facebookUrl} target="_blank" rel="noopener noreferrer"
                  className="text-neutral-400 hover:text-blue-600 transition-colors">
                  <FacebookLogo className="w-5 h-5" weight="fill" />
                </a>
              )}
              {store.tiktokUrl && (
                <a href={store.tiktokUrl} target="_blank" rel="noopener noreferrer"
                  className="text-neutral-400 hover:text-neutral-900 transition-colors">
                  <TiktokLogo className="w-5 h-5" weight="fill" />
                </a>
              )}
              {store.whatsappNumber && (
                <a href={`https://wa.me/${store.whatsappNumber.replace(/\D/g, '')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-neutral-400 hover:text-green-500 transition-colors">
                  <WhatsappLogo className="w-5 h-5" weight="fill" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Search + Browse Nav ───────────────────────────────────────── */}
      <div className="bg-white border-b border-neutral-100 sticky top-14 z-30">
        <div className="max-w-5xl mx-auto px-4 pt-3 pb-3 space-y-2">
          {/* Search row */}
          <div className="flex gap-2">
            <button
              onClick={() => { setTrackPrefill({ orderNumber: '', phone: '' }); setTrackOpen(true); }}
              className="sm:hidden flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all"
              style={{ borderColor: accent, color: accent }}
            >
              <ClockCountdown weight="bold" className="w-4 h-4" />
              Track
            </button>
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
              <input
                type="text" value={search}
                onChange={(e) => { setSearch(e.target.value); if (e.target.value) setView('browse'); }}
                placeholder="Search products..."
                className="w-full pl-10 pr-9 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 transition"
                style={{ '--tw-ring-color': accent + '30' }}
              />
              {search && (
                <button onClick={() => setSearch('')}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors">
                  <X weight="bold" className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Browse mode: back button + category pills */}
          {view === 'browse' && (
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-0.5">
              <button
                onClick={goHome}
                className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-neutral-800 transition-colors pr-2.5 border-r border-neutral-200 mr-0.5"
              >
                <ArrowLeft className="w-3.5 h-3.5" weight="bold" /> Home
              </button>
              <button
                onClick={() => setSelectedCategories(new Set())}
                className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-all"
                style={selectedCategories.size === 0
                  ? { backgroundColor: accent, color: '#fff' }
                  : { backgroundColor: '#f5f5f5', color: '#6b7280' }}
              >
                All
              </button>
              {categories.map((cat) => {
                const isActive = selectedCategories.has(cat.id);
                return (
                  <button key={cat.id}
                    ref={isActive ? (el) => el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' }) : null}
                    onClick={() => toggleCategory(cat.id)}
                    className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-all"
                    style={isActive
                      ? { backgroundColor: accent, color: '#fff' }
                      : { backgroundColor: '#f5f5f5', color: '#6b7280' }}
                  >
                    {cat.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Main Content ──────────────────────────────────────────────── */}
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-8">

        {view === 'home' ? (
          /* ── Home view: Featured + Categories ── */
          <>
            {featuredProducts.length > 0 && (
              <section>
                <h2 className="text-base font-bold text-neutral-900 mb-3">Featured</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {featuredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} store={store}
                      onSelect={setSelectedProduct} onAddToCart={addToCart} />
                  ))}
                </div>
              </section>
            )}

            {categories.length > 0 ? (
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-bold text-neutral-900">Shop by Category</h2>
                  <button
                    onClick={() => browseTo(null)}
                    className="text-sm font-semibold hover:opacity-70 transition-opacity"
                    style={{ color: accent }}
                  >
                    See All
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {categories.map((cat) => (
                    <CategoryCard key={cat.id} category={cat}
                      products={productsByCategory[cat.id] || []}
                      accent={accent}
                      selected={false}
                      onViewAll={() => browseTo(cat.id)} />
                  ))}
                </div>
              </section>
            ) : (
              /* No categories – show all products directly on home */
              <section>
                <h2 className="text-base font-bold text-neutral-900 mb-3">All Products</h2>
                {loadingProducts ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="rounded-2xl overflow-hidden animate-pulse">
                        <div className="aspect-square bg-neutral-100" />
                        <div className="p-3 space-y-2">
                          <div className="h-3 bg-neutral-100 rounded w-1/2" />
                          <div className="h-4 bg-neutral-100 rounded w-3/4" />
                          <div className="h-4 bg-neutral-100 rounded w-1/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : products.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
                    <SmileyMeh className="w-16 h-16 text-neutral-200" weight="duotone" />
                    <p className="text-neutral-500 font-medium">No products available right now.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {products.map((product) => (
                      <ProductCard key={product.id} product={product} store={store}
                        onSelect={setSelectedProduct} onAddToCart={addToCart} />
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        ) : (
          /* ── Browse view: filtered product grid ── */
          <section>
            {search ? (
              <p className="text-sm text-neutral-500 mb-4">
                {displayProducts.length} result{displayProducts.length !== 1 ? 's' : ''} for "{search}"
              </p>
            ) : null}
            {loadingProducts ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="rounded-2xl overflow-hidden animate-pulse">
                    <div className="aspect-square bg-neutral-100" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 bg-neutral-100 rounded w-1/2" />
                      <div className="h-4 bg-neutral-100 rounded w-3/4" />
                      <div className="h-4 bg-neutral-100 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : displayProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
                <SmileyMeh className="w-16 h-16 text-neutral-200" weight="duotone" />
                <p className="text-neutral-500 font-medium">
                  {search ? `No results for "${search}"` : 'No products in this category.'}
                </p>
                {search && (
                  <button onClick={() => setSearch('')} className="text-sm underline transition-colors" style={{ color: accent }}>
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {displayProducts.map((product) => (
                  <ProductCard key={product.id} product={product} store={store}
                    onSelect={setSelectedProduct} onAddToCart={addToCart} />
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      {/* ── Floating cart bar (mobile) ─────────────────────────────────── */}
      {cartCount > 0 && !cartOpen && !orderFormOpen && !confirmedOrder && (
        <div className="fixed bottom-5 inset-x-4 z-40 sm:hidden">
          <button
            onClick={() => setCartOpen(true)}
            className="w-full flex items-center justify-between gap-2 px-5 py-3.5 rounded-2xl shadow-xl text-sm font-bold text-white active:scale-95 transition-transform"
            style={{ backgroundColor: accent }}
          >
            <div className="flex items-center gap-2">
              <ShoppingCart weight="bold" className="w-5 h-5 flex-shrink-0" />
              <span>View Order ({cartCount})</span>
            </div>
            <span style={{ color: 'rgba(255,255,255,0.85)' }}>
              {fmt(cart.reduce((s, i) => s + getEffectivePrice(i) * i.quantity, 0), store.currencySymbol)}
            </span>
          </button>
        </div>
      )}

      {/* ── Also Available At (SPLIT mode only) ────────────────────────── */}
      {storefrontMode === 'SPLIT' && branchSlug && branches.length > 1 && (
        <AlsoAvailableAt
          branches={branches}
          currentBranchSlug={branchSlug}
          slug={slug}
          onSwitchBranch={handleBranchSwitch}
          accent={accent}
        />
      )}

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer className="mt-12 pb-24 sm:pb-8 text-center">
        <a href="https://smartposafrica.com" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-600 transition-colors">
          <span>Powered by</span>
          <span className="font-bold text-neutral-600">SmartPOS Africa</span>
        </a>
      </footer>

      {/* ── Overlays ──────────────────────────────────────────────────── */}
      {selectedProduct && (
        <ProductModal product={selectedProduct} store={store}
          onClose={() => setSelectedProduct(null)} onAddToCart={addToCart} />
      )}

      {cartOpen && (
        <CartDrawer cart={cart} store={store}
          onClose={() => setCartOpen(false)}
          onUpdateQty={updateQty} onRemove={removeItem}
          onCheckout={() => { setCartOpen(false); setOrderFormOpen(true); }} />
      )}

      {orderFormOpen && !confirmedOrder && (
        <OrderForm cart={cart} store={store} branchSlug={branchSlug}
          onClose={() => setOrderFormOpen(false)}
          onOrderPlaced={(order) => {
            setConfirmedOrder(order);
            setOrderFormOpen(false);
            setCart([]);
          }} />
      )}

      {/* ── Branch switch warning ──────────────────────────────────────── */}
      {switchWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSwitchWarning(null)} />
          <div className="relative bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl">
            <h3 className="text-base font-bold text-neutral-900 mb-2">Switch branch?</h3>
            <p className="text-sm text-neutral-500 mb-5">
              Switching to another branch will clear your current cart. You can't mix items from different branches.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setSwitchWarning(null)}
                className="flex-1 py-3 rounded-2xl text-sm font-bold bg-neutral-100 text-neutral-700 hover:bg-neutral-200 transition-colors">
                Keep cart
              </button>
              <button onClick={() => confirmBranchSwitch(switchWarning)}
                className="flex-1 py-3 rounded-2xl text-sm font-bold text-white hover:opacity-90 transition-colors"
                style={{ backgroundColor: accent }}>
                Switch branch
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmedOrder && (
        <OrderConfirmation order={confirmedOrder} store={store}
          onClose={() => setConfirmedOrder(null)}
          onTrack={() => {
            setTrackPrefill({ orderNumber: confirmedOrder.orderNumber, phone: confirmedOrder.customerPhone || '' });
            setConfirmedOrder(null);
            setTrackOpen(true);
          }} />
      )}

      {trackOpen && (
        <TrackOrderModal store={store} onClose={() => setTrackOpen(false)}
          prefillOrderNumber={trackPrefill.orderNumber}
          prefillPhone={trackPrefill.phone} />
      )}
    </div>
  );
};

export default Storefront;
